import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetchJson } from '../../lib/apiFetch.js';

/**
 * Site visits the crew reported back from that nobody has priced yet.
 *
 * This is the queue the field-report feature exists to produce. A visit already cost a truck, an
 * hour and a technician; if it never becomes a bid, that spend bought nothing. The server decides
 * membership on two facts — a report came back, and no estimate has been started — so there is no
 * client-side filtering here and no done-flag to keep in step.
 *
 * TODO(contracts): move these shapes into `@hbs-us/contracts` once the dispatch surface is
 * published. `packages/contracts/src/api/dispatch.ts` does not exist on develop yet (it is being
 * added on a branch), so declaring them there now would collide.
 */

export interface NeedsEstimateVisit {
  assignmentId: number;
  /** The day the crew was booked for. */
  scheduledDate: string;
  /** When the crew sent their report — what the queue is ordered by, oldest first. */
  reportedAt: string;
  /** The crew's own words. Internal reference: never printed on the customer's estimate. */
  fieldNotes: string;
  crewName: string;
  jobId: number | null;
  jobName: string | null;
  customerName: string | null;
  customerPhone: string | null;
  customerAddress: string | null;
}

interface NeedsEstimateResponse {
  ok: boolean;
  error?: string;
  visits?: NeedsEstimateVisit[];
}

interface StartEstimateResponse {
  ok: boolean;
  error?: string;
  estimateId?: number;
  estimateNumber?: string;
  /** False when the visit already had an estimate — the existing one is handed back instead. */
  created?: boolean;
}

export function needsEstimateQueryKey(): readonly unknown[] {
  return ['dispatch', 'needs-estimate'] as const;
}

export function useNeedsEstimateQuery() {
  return useQuery<NeedsEstimateResponse>({
    queryKey: needsEstimateQueryKey(),
    queryFn: () => apiFetchJson<NeedsEstimateResponse>('/api/v1/dispatch/needs-estimate'),
  });
}

/**
 * Start an estimate from a reported visit.
 *
 * The server carries the customer, phone and site address across and deliberately does NOT carry
 * the crew's report into the estimate's own fields — `scope_of_work` prints on the document the
 * customer reads. The report is shown beside the form instead.
 *
 * Creating the estimate is what removes the visit from the queue, so both the queue and the
 * estimates list are invalidated on success.
 */
export function useStartEstimateFromVisit() {
  const queryClient = useQueryClient();

  return useMutation<StartEstimateResponse, Error, { assignmentId: number }>({
    mutationFn: ({ assignmentId }) =>
      apiFetchJson<StartEstimateResponse>(`/api/v1/dispatch/${assignmentId}/estimate`, {
        method: 'POST',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: needsEstimateQueryKey() });
      void queryClient.invalidateQueries({ queryKey: ['estimates'] });
    },
  });
}
